/**
 * Service types (ops insights, Phase 1): the inputs that manage a shop's list. A leaf module
 * like customField.ts: it takes the idempotency envelope from the spine and nothing imports
 * back.
 *
 * Every input is `.strict()` (the 004 lesson; see asset.ts). Here it carries a rule as well:
 * the KEY is frozen at creation, so an input that names one is refused rather than having the
 * key silently stripped and the call answered 200. None of these inputs has a key to set.
 */
import { z } from 'zod';
import { idempotency } from './spine';
import { PRICING_BASES } from '../pricing-basis';

/**
 * The most service types a shop can have at once. A picker with more is past usable, and the
 * reorder input sends the whole list, so the two limits have to be the same number.
 */
export const SERVICE_TYPE_LIMIT = 100;

/** The longest name a service type can have. The Settings box stops typing at the same length. */
export const SERVICE_TYPE_LABEL_MAX = 80;

const label = z.string().trim().min(1).max(SERVICE_TYPE_LABEL_MAX);
const serviceTypeId = z.string().uuid();

/** A new type. Its key is derived from the label on the server, once. */
export const serviceTypeCreateInput = idempotency.extend({ label }).strict();
export type ServiceTypeCreateInput = z.infer<typeof serviceTypeCreateInput>;

/** Rename it, turn it on or off, change how its jobs bill parts, or any of these. Never its key. */
export const serviceTypeUpdateInput = idempotency
  .extend({
    serviceTypeId,
    label: label.optional(),
    active: z.boolean().optional(),
    /** For NEW jobs of this type; jobs already booked keep theirs (Inventory v2 Phase 1). */
    pricingBasis: z.enum(PRICING_BASES).optional(),
    /**
     * The hourly rate a T&M job of this type bills its hours at, in cents (ticket 13); null for
     * the shop's rate. Hours not billed yet start at the new rate.
     */
    laborRateCents: z.coerce.number().int().min(1).max(100_000_000).nullable().optional(),
  })
  .strict()
  .refine(
    (v) =>
      v.label !== undefined ||
      v.active !== undefined ||
      v.pricingBasis !== undefined ||
      v.laborRateCents !== undefined,
    {
      message:
        'Say what to change: a new name, whether it is in use, how its parts are priced, or its hourly rate',
    },
  );
export type ServiceTypeUpdateInput = z.infer<typeof serviceTypeUpdateInput>;

/**
 * "Remind after N months" (MAP 249 ticket 06): when a customer whose last job of this type was N
 * months ago gets a service reminder; null for never. Its own input, and its own procedure
 * (`serviceType.setRemindAfter`, the OWNER's: turning it on emails customers on a schedule), so
 * the office's `serviceType.update` never writes it (that input is `.strict()` and has no such
 * key). 1 to 36, the column's CHECK.
 */
export const serviceTypeRemindInput = idempotency
  .extend({
    serviceTypeId,
    remindAfterMonths: z.number().int().min(1).max(36).nullable(),
  })
  .strict();
export type ServiceTypeRemindInput = z.infer<typeof serviceTypeRemindInput>;

/**
 * The shop's whole live list, in its new order. The whole list rather than one row's new
 * position, the way checklist items are saved: the order sent IS the order stored, so two
 * rows can never end up sharing a place, and a list that changed since the screen loaded is
 * refused instead of half-applied.
 */
export const serviceTypeReorderInput = idempotency
  .extend({
    orderedIds: z.array(serviceTypeId).min(1).max(SERVICE_TYPE_LIMIT),
  })
  .strict();
export type ServiceTypeReorderInput = z.infer<typeof serviceTypeReorderInput>;

export const serviceTypeIdInput = idempotency.extend({ serviceTypeId }).strict();
export type ServiceTypeIdInput = z.infer<typeof serviceTypeIdInput>;
